/**
 * Hook for managing referral program data and actions.
 * Handles referral codes, stats, history, and rewards.
 */

import { useState, useEffect, useCallback } from "react";
import { apiGet, apiPost } from "../lib/api";

// ----- Types -----

export type ReferralStatus = "pending" | "completed" | "expired" | "revoked";
export type RewardStatus = "pending" | "applied" | "expired" | "voided";

export interface ReferralCode {
  code: string;
  shareUrl: string;
  totalUses: number;
  isActive: boolean;
  createdAt: string;
}

export interface ReferralStats {
  totalReferrals: number;
  completedReferrals: number;
  pendingReferrals: number;
  totalRewardsEarned: number;
  totalRewardValueCents: number;
  rewardsThisYear: number;
  rewardsRemaining: number;
  hasActiveCode: boolean;
}

export interface ReferralReward {
  id: string;
  rewardType: string;
  rewardDescription: string;
  rewardValueCents: number;
  status: RewardStatus;
  appliedAt: string | null;
  expiresAt: string | null;
}

export interface Referral {
  id: string;
  referredName: string;
  status: ReferralStatus;
  referredAt: string;
  convertedAt: string | null;
  rewardEarned: ReferralReward | null;
}

export interface ReferralValidation {
  valid: boolean;
  code: string;
  referrerName?: string;
  reason?: string;
  reward?: {
    description: string;
    valueCents: number;
  };
}

export interface ReferralEligibility {
  eligible: boolean;
  reason?: string;
  code?: string;
}

// API Response types
interface MyCodeResponse {
  code: string;
  shareUrl: string;
  totalUses: number;
  isActive: boolean;
  createdAt: string;
}

interface StatsResponse {
  totalReferrals: number;
  completedReferrals: number;
  pendingReferrals: number;
  totalRewardsEarned: number;
  totalRewardValueCents: number;
  rewardsThisYear: number;
  rewardsRemaining: number;
  hasActiveCode: boolean;
}

interface HistoryResponse {
  referrals: Referral[];
  total: number;
  limit: number;
  offset: number;
}

interface RewardsResponse {
  rewards: ReferralReward[];
  summary: {
    totalPending: number;
    totalApplied: number;
    totalExpired: number;
    pendingValueCents: number;
    appliedValueCents: number;
  };
}

interface GenerateCodeResponse {
  code: string;
  shareUrl: string;
  createdAt: string;
}

// ----- Hook -----

export function useReferrals() {
  const [code, setCode] = useState<ReferralCode | null>(null);
  const [stats, setStats] = useState<ReferralStats | null>(null);
  const [history, setHistory] = useState<Referral[]>([]);
  const [rewards, setRewards] = useState<ReferralReward[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      // Fetch all referral data in parallel
      const [codeResult, statsResult, historyResult, rewardsResult] =
        await Promise.all([
          apiGet<MyCodeResponse>("/api/referrals/my-code"),
          apiGet<StatsResponse>("/api/referrals/stats"),
          apiGet<HistoryResponse>("/api/referrals/history"),
          apiGet<RewardsResponse>("/api/referrals/rewards"),
        ]);

      // Code may not exist yet, that's ok
      if (codeResult.data) {
        setCode({
          code: codeResult.data.code,
          shareUrl: codeResult.data.shareUrl,
          totalUses: codeResult.data.totalUses,
          isActive: codeResult.data.isActive,
          createdAt: codeResult.data.createdAt,
        });
      } else {
        setCode(null);
      }

      // Stats should always be available
      if (statsResult.data) {
        setStats(statsResult.data);
      } else {
        // Default stats for users without any referral activity
        setStats({
          totalReferrals: 0,
          completedReferrals: 0,
          pendingReferrals: 0,
          totalRewardsEarned: 0,
          totalRewardValueCents: 0,
          rewardsThisYear: 0,
          rewardsRemaining: 12,
          hasActiveCode: false,
        });
      }

      // History may be empty
      if (historyResult.data) {
        setHistory(historyResult.data.referrals);
      } else {
        setHistory([]);
      }

      // Rewards may be empty
      if (rewardsResult.data) {
        setRewards(rewardsResult.data.rewards);
      } else {
        setRewards([]);
      }
    } catch (err) {
      console.error("Referrals fetch error:", err);
      setError(err instanceof Error ? err.message : "Failed to load referral data");
    } finally {
      setIsLoading(false);
    }
  }, []);

  const generateCode = useCallback(async (): Promise<ReferralCode> => {
    setIsGenerating(true);
    try {
      const result = await apiPost<GenerateCodeResponse, Record<string, never>>(
        "/api/referrals/generate-code",
        {}
      );

      if (result.error) {
        throw new Error(result.error.message);
      }

      if (!result.data) {
        throw new Error("Failed to generate referral code");
      }

      const newCode: ReferralCode = {
        code: result.data.code,
        shareUrl: result.data.shareUrl,
        totalUses: 0,
        isActive: true,
        createdAt: result.data.createdAt,
      };

      setCode(newCode);
      return newCode;
    } finally {
      setIsGenerating(false);
    }
  }, []);

  const validateCode = useCallback(
    async (codeToValidate: string): Promise<ReferralValidation> => {
      const result = await apiPost<ReferralValidation, { code: string }>(
        "/api/referrals/validate",
        { code: codeToValidate }
      );

      if (result.error) {
        return {
          valid: false,
          code: codeToValidate,
          reason: result.error.message,
        };
      }

      return result.data!;
    },
    []
  );

  const applyCode = useCallback(
    async (codeToApply: string): Promise<{ applied: boolean; message?: string }> => {
      const result = await apiPost<
        { applied: boolean; code: string; reward?: { description: string } },
        { code: string }
      >("/api/referrals/apply", { code: codeToApply });

      if (result.error) {
        return { applied: false, message: result.error.message };
      }

      return { applied: result.data?.applied ?? false };
    },
    []
  );

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return {
    code,
    stats,
    history,
    rewards,
    isLoading,
    error,
    isGenerating,
    generateCode,
    validateCode,
    applyCode,
    refetch: fetchData,
  };
}

export default useReferrals;
